import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import Avatar from '../components/Avatar';
import { AVATAR_PALETTE, defaultAvatarColor } from '../utils/avatarColors';
import { sent as fetchSent, withdrawSuggestion } from '../services/suggestions';
import { formatPrice } from '../utils/wishDisplay';

export default function Profile() {
    const { user, updateUser, logout } = useAuth();
    const navigate = useNavigate();
    const fileInput = useRef(null);

    const [form, setForm] = useState({
        name: user?.name || '',
        birthday: user?.birthday ? String(user.birthday).slice(0, 10) : '',
        phone: user?.phone || '',
    });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [savingColor, setSavingColor] = useState(false);
    const [message, setMessage] = useState(null); // { type, text }

    // "Suggestions I sent" — the sender's list with statuses.
    const [sentSuggestions, setSentSuggestions] = useState([]);
    const [withdrawingId, setWithdrawingId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchSent()
            .then((list) => {
                if (!cancelled) setSentSuggestions(list || []);
            })
            .catch(() => {
                if (!cancelled) setSentSuggestions([]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleWithdraw = async (suggestion) => {
        if (!window.confirm(`Withdraw the recommendation "${suggestion.title}"?`)) return;
        setWithdrawingId(suggestion.id);
        try {
            await withdrawSuggestion(suggestion.id);
            setSentSuggestions((prev) =>
                prev.map((s) => (s.id === suggestion.id ? { ...s, status: 'withdrawn' } : s))
            );
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Could not withdraw the recommendation.' });
        } finally {
            setWithdrawingId(null);
        }
    };

    const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            const res = await api.put('/user', {
                name: form.name,
                birthday: form.birthday || null,
                phone: form.phone || null,
            });
            updateUser(res.data);
            setMessage({ type: 'success', text: 'Profile saved.' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Could not save profile.' });
        } finally {
            setSaving(false);
        }
    };

    const handleAvatar = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setMessage(null);
        try {
            const data = new FormData();
            data.append('avatar', file);
            const res = await api.post('/user/avatar', data, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            updateUser(res.data);
            setMessage({ type: 'success', text: 'Photo updated.' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Could not upload photo.' });
        } finally {
            setUploading(false);
            if (fileInput.current) fileInput.current.value = '';
        }
    };

    // Save the chosen badge color immediately. `hex` is a "#RRGGBB" from the
    // palette, or null to fall back to the deterministic default.
    const handleColor = async (hex) => {
        setSavingColor(true);
        setMessage(null);
        try {
            const res = await api.put('/user', { avatar_color: hex });
            updateUser(res.data);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Could not save color.' });
        } finally {
            setSavingColor(false);
        }
    };

    const handleSignOut = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <AppLayout maxWidth="max-w-2xl">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">My Profile</h2>

                {message && (
                    <div
                        className={`mb-4 p-3 rounded-md text-sm border ${
                            message.type === 'success'
                                ? 'bg-green-50 border-green-200 text-green-700'
                                : 'bg-red-50 border-red-200 text-red-700'
                        }`}
                    >
                        {message.text}
                    </div>
                )}

                {/* Avatar */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex items-center gap-5">
                        <Avatar name={user?.name} url={user?.avatar_url} color={user?.avatar_color} seed={user?.id} size={72} />
                        <div>
                            <input ref={fileInput} type="file" accept="image/*" onChange={handleAvatar} className="hidden" />
                            <button
                                onClick={() => fileInput.current?.click()}
                                disabled={uploading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                            >
                                {uploading ? 'Uploading…' : user?.avatar_url ? 'Change photo' : 'Upload photo'}
                            </button>
                            <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP or GIF, up to 4 MB.</p>
                        </div>
                    </div>

                    {/* Badge color — only relevant while there's no photo to show. */}
                    {!user?.avatar_url && (
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Badge color</label>
                            <p className="text-xs text-gray-400 mb-3">Shown behind your initials when you don't have a photo.</p>
                            <div className="flex flex-wrap gap-2">
                                {/* "Default" — clears avatar_color; shows the id-derived color. */}
                                <ColorSwatch
                                    color={defaultAvatarColor(user?.id)}
                                    selected={!user?.avatar_color}
                                    isDefault
                                    disabled={savingColor}
                                    onClick={() => handleColor(null)}
                                />
                                {AVATAR_PALETTE.map(({ name, hex }) => (
                                    <ColorSwatch
                                        key={hex}
                                        color={hex}
                                        title={name}
                                        selected={user?.avatar_color?.toUpperCase() === hex.toUpperCase()}
                                        disabled={savingColor}
                                        onClick={() => handleColor(hex)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Profile fields */}
                <form onSubmit={handleSave} className="bg-white rounded-lg shadow-sm p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                            type="text"
                            required
                            value={form.name}
                            onChange={(e) => setField('name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                            type="email"
                            value={user?.email || ''}
                            disabled
                            className="w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-500 rounded-md"
                        />
                        <p className="text-xs text-gray-400 mt-1">Email can't be changed here yet.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Birthday</label>
                            <input
                                type="date"
                                value={form.birthday}
                                onChange={(e) => setField('birthday', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-400 mt-1">Powers birthday reminders &amp; gift reveal dates.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input
                                type="text"
                                value={form.phone}
                                onChange={(e) => setField('phone', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </form>

                {/* Ideas I've suggested to others, with their status. */}
                {sentSuggestions.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Recommendations I sent</h3>
                        <div className="space-y-2">
                            {sentSuggestions.map((s) => {
                                const price = formatPrice(s.price_min, s.price_max);
                                return (
                                    <div key={s.id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                                        <Avatar name={s.recipient?.name} url={s.recipient?.avatar_url} color={s.recipient?.avatar_color} seed={s.recipient?.id} size={32} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{s.title}</p>
                                            <p className="text-xs text-gray-500 truncate">
                                                For {s.recipient?.name || 'someone'}
                                                {price ? ` · ${price}` : ''}
                                            </p>
                                        </div>
                                        <SuggestionStatusPill status={s.status} />
                                        {s.status === 'pending' && (
                                            <button
                                                onClick={() => handleWithdraw(s)}
                                                disabled={withdrawingId === s.id}
                                                className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                                            >
                                                Withdraw
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Sign out lives on the Profile page, mirroring iOS. */}
                <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
                    <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-2 text-red-600 hover:bg-red-50 rounded-md font-medium"
                    >
                        Sign Out
                    </button>
                </div>
        </AppLayout>
    );
}

// Status pill for a sent suggestion: amber pending / green accepted / gray
// declined or withdrawn (matching the app's button color language).
function SuggestionStatusPill({ status }) {
    const map = {
        pending: { text: 'Pending', cls: 'bg-amber-100 text-amber-700' },
        accepted: { text: 'Accepted', cls: 'bg-green-100 text-green-700' },
        declined: { text: 'Declined', cls: 'bg-gray-100 text-gray-600' },
        withdrawn: { text: 'Withdrawn', cls: 'bg-gray-100 text-gray-500' },
    };
    const { text, cls } = map[status] || map.declined;
    return <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${cls}`}>{text}</span>;
}

// A single round color chip in the badge-color picker. `isDefault` marks the
// "use the automatic color" option (which clears avatar_color to null).
function ColorSwatch({ color, title, selected, isDefault, disabled, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={isDefault ? 'Default (automatic)' : title}
            aria-label={isDefault ? 'Default color' : title}
            aria-pressed={selected}
            style={{ backgroundColor: color }}
            className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold transition disabled:opacity-50 ${
                selected ? 'ring-2 ring-offset-2 ring-gray-500' : ''
            }`}
        >
            {isDefault ? 'A' : selected ? '✓' : ''}
        </button>
    );
}
